import { emsMediumGrey } from "@electricMotorShop/EmsVariables";
import EMSDelivery from "@electricMotorShop/Icons/EMSDelivery";
import EMSPickup from "@electricMotorShop/Icons/EMSPickup";
import EMSShipping from "@electricMotorShop/Icons/EMSShipping";
import setInventoryIsLoading from "@electricMotorShop/Store/Components/ProductAvailability/Handlers/SetInventoryIsLoading";
import { CustomProductAvailabilityState } from "@electricMotorShop/Store/Components/ProductAvailability/ProductAvailabilityReducer";
import {
    showInventoryBasedOnAvailability,
} from "@electricMotorShop/Store/Data/Products/XcProductsSelectors";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconMemo, IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Check from "@insite/mobius/Icons/Check";
import MapPin from "@insite/mobius/Icons/MapPin";
import X from "@insite/mobius/Icons/X";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Tooltip from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import safeColor from "@insite/mobius/utilities/safeColor";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

// XCentium customization to load all warehouse data for items

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    infoWarehouses?: WarehouseDto[];
    productInfo?: ProductInfo;
}

const mapStateToProps = (state: CustomProductAvailabilityState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    showInventory: state.context.showInventory,
    productList: state.pages.productList,
    isLoading: state.xcentiumProductAvailability.isLoading,
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
    setInventoryIsLoading,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    labelGridItem?: GridItemProps;
    noShipLabelGridItem?: GridItemProps;
    messageGridItem?: GridItemProps;
    messageWrapper?: InjectableCss;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    warehouseText?: TypographyPresentationProps;
    shippingText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    pickupIcon?: IconPresentationProps;
    deliveryIcon?: IconPresentationProps;
    shippingIcon?: IconPresentationProps;
    noShipIcon?: IconPresentationProps;
    noShipText?: TypographyPresentationProps;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            border: 1px solid ${emsMediumGrey};
            border-radius: 4px;
            padding: 2px 5px;
            margin-bottom: 10px;
        `,
    },
    labelGridItem: {
        width: 12,
        css: css`
            font-weight: bold;
        `,
    },
    noShipLabelGridItem: {
        width: 12,
        css: css`
            font-weight: bold;
            color: ${emsMediumGrey};
        `,
    },
    messageGridItem: { width: 12 },
    messageWrapper: {
        css: css`
            padding: 0 6px;
            display: inline-flex;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        css: css`
            width: 100%;
            margin-right: 4px;
            ${wrapInContainerStyles}
        `,
    },
    warehouseText: {
        css: css`
            width: 100%;
            font-size: 12px;
            margin-left: 3px;
            font-weight: bold;
        `,
    },
    shippingText: {
        css: css`
            width: 100%;
            font-size: 15px;
            margin-left: 3px;
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        weight: "bold",
    },
    pickupIcon: {
        src: EMSPickup,
        css: css`
            margin: 2px 5px 2px 0;
            height: 18px;
            width: 18px;
            color: ${getColor("primary.main")};
        `,
    },
    deliveryIcon: {
        src: EMSDelivery,
        css: css`
            margin: 2px 5px 2px 8px;
            height: 18px;
            width: 18px;
            color: ${getColor("primary.main")};
        `,
    },
    shippingIcon: {
        src: EMSShipping,
        css: css`
            height: 20px;
            width: 24px;
            margin-right: 5px;
            color: ${getColor("primary.main")};
        `,
    },
    noShipIcon: {
        src: EMSShipping,
        css: css`
            height: 20px;
            width: 24px;
            margin-right: 5px;
            color: ${emsMediumGrey};
        `,
    },
    noShipText: {
        color: emsMediumGrey,
    },
};

const InventoryMessage = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    padding: 5px;
    border-radius: 4px;
    margin-bottom: 2px;
    background-color: ${props => safeColor(resolveColor(props.color, props.theme)).rgb().alpha(0.2).string()};
    ${IconWrapper} {
        margin-right: 5px;
    }
`;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "danger";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "success";
};

const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return X;
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return AlertTriangle;
    }

    return Check;
};

const ProductAvailability = ({
    availability,
    productSettings,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
    productInfo,
    infoWarehouses,
}: Props) => {
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (infoWarehouses) {
            setWarehouses(infoWarehouses);
        } else if (productInfo && productInfo.inventory && productInfo.inventory.inventoryWarehousesDtos !== null) {
            const inventoryWarehouseDtos = productInfo.inventory.inventoryWarehousesDtos?.filter(
                x => x.unitOfMeasure === productInfo.unitOfMeasure,
            );
            if (
                inventoryWarehouseDtos && inventoryWarehouseDtos.length > 0 &&
                inventoryWarehouseDtos[0] !== null &&
                inventoryWarehouseDtos[0].warehouseDtos !== null
            ) {
                setWarehouses(inventoryWarehouseDtos[0].warehouseDtos);
            }
        }
    }, [productInfo]);

    const color = getAvailabilityColor(productSettings, availability);
    const iconSrc = getAvailabilityIcon(productSettings, availability);

    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));
    if (!showInventory) {
        return null;
    }

    if (failedToLoadInventory) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...styles.errorText} data-test-selector="inventoryLoadFailedMessage">
                    {siteMessage("RealTimeInventory_InventoryLoadFailed")}
                </Typography>
            </StyledWrapper>
        );
    }

    const locationTitle = "My Location";

    const shippingQty = warehouses
        ? warehouses
              ?.map(a => a.qty)
              .reduce(function (a, b) {
                  return a + b;
              })
        : 0;
    const shippingInventoryText = `${shippingQty} available`;

    let noShip = false;
    let availabilityMessage;
    if (!showInventoryBasedOnAvailability(productInfo)) {
        return null;
    }

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    if (availability?.messageType !== AvailabilityMessageType.NoMessage && availability?.message?.length) {
        // if message has noship prefix, set noShip to true and remove the prefix
        if (availability.message.indexOf("NoShip:") > -1) {
            noShip = true;
            availabilityMessage = availability.message.split("NoShip:")[1];
        } else {
            availabilityMessage = availability.message;
        }

        // Pickup or delivery component
        inventoryTextComponent = (
            <InventoryMessage color={color} {...styles.inventoryMessage}>
                <Icon height={22} width={22} color={color} src={iconSrc} />
                <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                    {availabilityMessage}
                </Typography>

                <Tooltip iconProps={{ src: MapPin, color: "black" }} text={translate(locationTitle)} />
            </InventoryMessage>
        );
    } else if (availability) {
        inventoryTextComponent = <></>;
    }

    return (
        <>
            <GridContainer {...styles.container}>
                <GridItem {...styles.labelGridItem}>
                    <IconMemo {...styles.pickupIcon} />
                    Pickup or <IconMemo {...styles.deliveryIcon} /> Delivery
                </GridItem>
                <GridItem {...styles.messageGridItem}>{inventoryTextComponent}</GridItem>
                {errorMessage ? (
                    <GridItem>{errorMessage}</GridItem>
                ) : (
                    warehouses
                        ?.filter(x => availability?.message?.indexOf(x.description) === -1)
                        ?.map(warehouse => (
                            <GridItem {...styles.messageGridItem} key={`${warehouse.description}`}>
                                <StyledWrapper {...styles.messageWrapper}>
                                    <Icon
                                        height={18}
                                        width={18}
                                        color={warehouse.qty > 0 ? "green" : "red"}
                                        src={warehouse.qty > 0 ? Check : X}
                                    />
                                    <Typography {...styles.warehouseText}>
                                        {warehouse.qty} available for transfer at {warehouse.description}
                                    </Typography>
                                </StyledWrapper>
                            </GridItem>
                        ))
                )}
            </GridContainer>
            {!noShip && (
                <GridContainer {...styles.container}>
                    <GridItem {...styles.labelGridItem}>
                        <IconMemo {...styles.shippingIcon} />
                        Shipping
                    </GridItem>
                    <GridItem {...styles.messageGridItem}>
                        <StyledWrapper {...styles.messageWrapper}>
                            <Icon
                                height={24}
                                width={24}
                                color={shippingQty > 0 ? "green" : "red"}
                                src={shippingQty > 0 ? Check : X}
                            />
                            <Typography {...styles.shippingText}>{shippingInventoryText}</Typography>
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            )}
            {noShip && (
                <GridContainer {...styles.container}>
                    <GridItem {...styles.noShipLabelGridItem}>
                        <IconMemo {...styles.noShipIcon} />
                        Shipping
                    </GridItem>
                    <GridItem {...styles.messageGridItem}>
                        <StyledWrapper {...styles.messageWrapper}>
                            <Icon height={24} width={24} color={emsMediumGrey} src={X} />
                            <Typography {...styles.noShipText}>Not available</Typography>
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
